import {Component, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import {classToClass, plainToClass} from "class-transformer";
import {NgForm} from '@angular/forms';
import {ConfirmationService, SelectItem} from "primeng/api";

import {UserSearch} from '@app/shared/classes/user';
import {NoTitlecaseInternalContactType, ProjectType} from '@app/shared/classes/common';
import {ContractEmit, InternalContact} from "@app/shared/classes/contract";
import {EnumType} from '@app/shared/enums/enum-type.enum';
import {Validation} from '@app/shared/enums/validation.enum';

import {GrowlService} from '@app/core';
import {CommonTransformationService} from "@app/shared/services/common-transformation.service";
import {CommonDataService} from "@app/shared/services/common-data.service";
import {InternalContactsService} from './internal-contacts-service';
import {GrowlType} from '../enums/growl-type.enum';
import {PrimeNgConfig} from "@app/shared/classes/prime-ng-config";
import {TableColumn} from "@app/shared";

@Component({
  selector: 'app-internal-contacts',
  templateUrl: './internal-contacts.component.html',
  styleUrls: ['./internal-contacts.component.css'],
  providers: [InternalContactsService]
})
export class InternalContactsComponent implements OnInit {

  @Input() contacts: InternalContact[];
  @Input() contactTypeId: number;
  @Input() contactType: ProjectType;
  @Input() contractId: number;

  @Output() updateHandler: EventEmitter<any> = new EventEmitter();

  NoTitlecaseInternalContactType = NoTitlecaseInternalContactType;
  ProjectType: typeof ProjectType = ProjectType;
  PrimeNgConfig = PrimeNgConfig;
  Validation: typeof Validation = Validation;

  contractContacts: SelectItem[];
  selectedContractContacts: InternalContact[];

  contactToEdit: InternalContact;
  tableColumns: TableColumn[];
  showDataTable: boolean = true;

  // dialog related attributes
  displayDialog: boolean;
  selectedContactToAdd: UserSearch;
  filteredContacts: UserSearch[];
  locationDropdown: SelectItem[];
  contactTypeDropdown: SelectItem[];

  @ViewChild('contactForm') contactForm: NgForm;


  constructor(private commonDataService: CommonDataService, private commonTransService: CommonTransformationService, private growl: GrowlService, private confirmationService: ConfirmationService, private internalContactsService: InternalContactsService) {
  }


  ngOnInit() {
    this.initTableColumns();
    this.buildDropdownMenus();
    this.getContractContacts(this.contractId, this.contactType);
  }

  /**
   * add selected contract contact(s) to the project.
   * @param contractContacts - selected contract contacts
   * @return handler - inform the parent component to save
   */
  onClickAddContractContact(contractContacts: InternalContact[]): void {
    let contactAdded = false;
    for (let selectedContact of contractContacts) {
      selectedContact.projectId = this.contactTypeId;
      let contactExists = false;
      for (const contact of this.contacts) {
        if (contact.employeeId == selectedContact.employeeId) {
          contactExists = true;
          break;
        }
      }
      if (!contactExists) {
        this.contacts.push(classToClass(selectedContact));
        contactAdded = true;
      } else {
        this.growl.add(GrowlType.Warn, `Member ${selectedContact.fullName} already exist`);
      }
    }
    if (contactAdded) {
      this.updateHandler.emit(ContractEmit.ContactSave);
    }
  }

  onClickDeleteContact(contact: InternalContact): void {
    this.confirmationService.confirm({
      message: `Are you sure that you want to delete this contact (${contact.fullName})?`,
      header: 'Delete Contact',
      icon: 'fa fa-question-circle',
      key: 'DeleteInternalContact',
      accept: () => {
        const index = this.contacts.findIndex(c => c.id == contact.id);
        if (index >= 0) {
          this.contacts.splice(index, 1);     // splice is okay since the contacts will be saved by the parent component and reloaded
          this.updateHandler.emit(ContractEmit.ContactDelete);
        }
      },
      reject: () => {
      }
    });
  }

  searchUser(query: string): void {
    this.commonDataService.searchUserByName(query).subscribe(res => {
      this.filteredContacts = plainToClass(UserSearch, res.list);
    });
  }

  onClickNewContact(contactType: ProjectType): void {
    this.contactToEdit = new InternalContact();
    this.contactToEdit.subtype = contactType;

    if (contactType == ProjectType.Contract) {
      this.contactToEdit.projectId = null;
      this.contactToEdit.estimateId = null;
      this.contactToEdit.contractId = this.contactTypeId;
    } else if (contactType == ProjectType.Project) {
      this.contactToEdit.projectId = this.contactTypeId;
      this.contactToEdit.estimateId = null;
      this.contactToEdit.contractId = null;
    } else if (contactType == ProjectType.ProjectEstimate) {
      this.contactToEdit.projectId = null;
      this.contactToEdit.contractId = null;
      this.contactToEdit.estimateId = this.contactTypeId;
    }

    this.displayDialog = true;
  }

  onSelectContactToAdd(selectedContact: UserSearch): void {
    this.contactToEdit.employeeId = selectedContact.id;
    this.contactToEdit.locationId = selectedContact.locationId;
    this.contactToEdit.fullName = selectedContact.fullName;
  }

  onChangeLocation(): void {
    for (const location of this.locationDropdown) {
      if (location['value'] == this.contactToEdit.locationId) {
        this.contactToEdit.addrName = location['label'];
      }
    }
  }

  onClickEditContact(contact: InternalContact): void {
    this.contactToEdit = classToClass(contact);
    this.displayDialog = true;
  }

  onClickAddContact(contact: InternalContact): void {
    let memberExists = false;
    for (const contact of this.contacts) {
      if (contact.employeeId == this.contactToEdit.employeeId) {
        memberExists = true;
      }
    }
    if (!memberExists) {
      this.contacts.push(contact);
      this.updateHandler.emit(ContractEmit.ContactSave);
      this.displayDialog = false;
    } else {
      this.growl.add(GrowlType.Error, 'Member already exist');
    }
  }

  onClickUpdateContact(contact: InternalContact): void {
    const index = this.contacts.findIndex(c => c.id === contact.id);
    if (index >= 0) {
      this.contacts[index] = contact;
      this.updateHandler.emit(ContractEmit.ContactUpdate);
    }
    this.displayDialog = false;
  }

  onClickCancelEdit(): void {
    this.displayDialog = false;
  }

  private initTableColumns(): void {
    this.tableColumns = [
      {field: 'fullName', header: 'Name'},
      {field: 'addrName', header: 'Location'},
      {field: 'contactType', header: 'Contact Type'},
      {field: 'action', header: 'Actions'},
    ];
  }

  private buildDropdownMenus(): void {
    this.commonDataService.getEnumPairAsDropdown(EnumType.InternalContact, true, NoTitlecaseInternalContactType).subscribe(res => {
      this.contactTypeDropdown = res;
    });
    this.commonDataService.getAllLocations().subscribe(res => {
      this.locationDropdown = this.commonTransService.convertToDropDown("addrName", res.list, "id");
    });
  }

  private getContractContacts(contractId: number, contactType: ProjectType): void {
    if (contractId && contactType == ProjectType.Project) {
      this.internalContactsService.getContractSupportTeam(this.contractId).subscribe(res => {
        this.contractContacts = this.commonTransService.convertToDropDown('fullName', res.list);
      });
    }
  }
}
